import type {
  PopperChildrenProps,
  PopperProps as ReactPopperProps,
  ReferenceChildrenProps,
} from 'react-popper'
import { Manager, Popper as ReactPopper, Reference } from 'react-popper'
import type { ReactNode } from 'react'
import React, { useRef } from 'react'
import { createPortal } from 'react-dom'
import { RemoveScroll } from 'react-remove-scroll'
import { useMediaQueryContext } from '../../providers/MediaQueryProvider'
import { useOutsideClick } from '../../hooks/useOutsideClick'
import * as S from './Popper.styled'

export type PopperProps = {
  isOpen: boolean
  referenceComponent: ReactNode
  children: ReactNode
  popperProps?: Omit<ReactPopperProps<unknown>, 'children'>
  style?: PopperChildrenProps['style']
  onClose: () => void
}

export const Popper = ({
  isOpen,
  children,
  referenceComponent,
  popperProps,
  style: childrenStyle,
  onClose,
}: PopperProps) => {
  const mq = useMediaQueryContext()
  const containerRef = useRef<HTMLDivElement>(null)
  const zIndex = childrenStyle?.zIndex || 10

  useOutsideClick(containerRef, () => {
    mq.tablet && onClose()
  })

  const renderPanel = ({ ref, style, placement: dataPlacement }: PopperChildrenProps) => {
    const styles = { ...style, ...childrenStyle, zIndex }

    return (
      <S.Panel ref={ref} data-placement={dataPlacement} style={styles}>
        {children}
      </S.Panel>
    )
  }

  const renderChildren = (props: PopperChildrenProps) => {
    return (
      <>
        {createPortal(
          <S.WrapperMobile id="bottomsheet" data-testid="Popper.WrapperMobile" style={{ zIndex }}>
            <S.Backdrop
              style={{ zIndex, margin: 0 }}
              onClick={(event) => {
                event.stopPropagation()
                onClose?.()
              }}
            />
            <S.BottomInset style={{ zIndex }} />
            {renderPanel(props)}
          </S.WrapperMobile>,
          document.body,
        )}
        <S.WrapperDesktop data-testid="Popper.WrapperDesktop" style={{ zIndex }}>
          {renderPanel(props)}
        </S.WrapperDesktop>
      </>
    )
  }

  return (
    <Manager>
      <div ref={containerRef}>
        <Reference>
          {({ ref }: ReferenceChildrenProps) => <div ref={ref}>{referenceComponent}</div>}
        </Reference>
        <RemoveScroll enabled={isOpen && !(mq.laptop || mq.desktop)}>
          {isOpen ? <ReactPopper {...popperProps}>{renderChildren}</ReactPopper> : null}
        </RemoveScroll>
      </div>
    </Manager>
  )
}
